import { Avatar, Box, Stack, Typography } from '@mui/material'
import { CollapsableRow, Column, Row } from '../../store/types/utils/DataTableUtils'
import { t } from 'i18next'
import TableCell from '@mui/material/TableCell'
import React, { ReactNode, useMemo } from 'react'
import CollapsibleTable from '../../components/Table/CollapsibleTable/CollapsibleTable'
import TransitionTableCell from '../../components/Table/CollapsibleTable/TransitionTableCell'
import { useGetPlannedSettlementsQuery } from '../../store/redux/reducers/workforceScheduleSlice'
import PlannedSettlement, { WorkforceScheduleLightReturnDTO } from '../../store/types/models/PlannedSettlement'
import dayjs from 'dayjs'
import DateFormat from '../../store/types/enums/DateFormat'
import { Settlement } from '../../store/types'

type MonthlyHours = {
  month: string
  hours: number
}

function transformPlannedResponses(responses: PlannedSettlement[], monthlyHours: MonthlyHours[]): Settlement[] {
  return responses.map(settlement => {
    let totalOperationalHoursPlanned = 0
    const totalHoursPlanned = monthlyHours.reduce((acc, { month, hours }) => {
      const filteredSchedule = settlement.workforceSchedule.filter(
        schedule => dayjs(schedule.date).format(DateFormat.YearMonth) === month
      )
      let totalOperationalPercent = 0
      const totalPercent = filteredSchedule.reduce((acc, schedule) => {
        if (schedule.project.operational) totalOperationalPercent = totalOperationalPercent + schedule.percent
        return acc + schedule.percent
      }, 0)
      const totalPlannedHours = (totalPercent / 100) * hours
      if (totalPercent > 0) {
        totalOperationalHoursPlanned =
          totalOperationalHoursPlanned + (totalPlannedHours * totalOperationalPercent) / totalPercent
      }
      return acc + totalPlannedHours
    }, 0)

    const totalHoursInGivenPeriod =
      (settlement.employmentPercentage / 100) *
      monthlyHours.reduce((acc, value) => {
        return acc + value.hours
      }, 0)
    const totalNonOperatingHours = totalHoursPlanned - totalOperationalHoursPlanned
    const totalMissingOrFreeHours = totalHoursInGivenPeriod - totalHoursPlanned
    const percentageHoursWorkedOrPlanned =
      totalHoursInGivenPeriod === 0 ? 0 : (totalHoursPlanned / totalHoursInGivenPeriod) * 100
    const percentageOperatingHours =
      totalHoursInGivenPeriod === 0 ? 0 : (totalOperationalHoursPlanned / totalHoursInGivenPeriod) * 100
    return {
      employee: settlement.employee,
      employmentPercentage: parseFloat(settlement.employmentPercentage.toFixed(1)),
      totalHoursInGivenPeriod: parseFloat(totalHoursInGivenPeriod.toFixed(1)),
      totalHoursWorkedOrPlanned: parseFloat(totalHoursPlanned.toFixed(1)),
      totalOperatingHours: parseFloat(totalOperationalHoursPlanned.toFixed(1)),
      totalNonOperatingHours: parseFloat(totalNonOperatingHours.toFixed(1)),
      totalMissingOrFreeHours: parseFloat(totalMissingOrFreeHours.toFixed(1)),
      percentageHoursWorkedOrPlanned: parseFloat(percentageHoursWorkedOrPlanned.toFixed(1)),
      percentageOperatingHours: parseFloat(percentageOperatingHours.toFixed(1)),
      workHoursOrSchedule: settlement.workforceSchedule,
    }
  })
}

type PlannedTableProps = {
  leaderId: string
  from: string
  to: string
  monthlyHours: { month: string; hours: number }[]
  renderFooter: (settlements: Settlement[]) => ReactNode
  searchText: string
  employmentTypeFilter: string | undefined
}

const PlanTable = ({
  leaderId,
  from,
  to,
  monthlyHours,
  renderFooter,
  searchText,
  employmentTypeFilter,
}: PlannedTableProps) => {
  const { data } = useGetPlannedSettlementsQuery({ id: leaderId, props: { fromDate: from, toDate: to } })

  const settlements = useMemo(() => {
    if (!data) return []

    const trimmedSearchText = searchText.trim().toLowerCase()
    const hasSearchText = !!trimmedSearchText
    const hasEmploymentFilter = !!employmentTypeFilter

    return transformPlannedResponses(data, monthlyHours).filter(value => {
      const { firstName, lastName, employmentType } = value.employee

      const matchesSearchText =
        !hasSearchText ||
        firstName.toLowerCase().includes(trimmedSearchText) ||
        lastName.toLowerCase().includes(trimmedSearchText)

      let matchesEmploymentType = !hasEmploymentFilter
      if (employmentType !== null) {
        matchesEmploymentType = !hasEmploymentFilter || employmentTypeFilter.includes(employmentType.value)
      }

      return matchesSearchText && matchesEmploymentType
    })
  }, [data, monthlyHours, searchText, employmentTypeFilter])

  const getHoursForMonth = (monthName: string) => {
    const result = monthlyHours.find(entry => entry.month === monthName)
    return result ? result.hours : 0
  }

  const columns: Column[] = [
    {
      id: 'employee',
      label: t('page.settlements.table.headers.employee'),
    },
    { id: 'employmentPercentage', label: t('page.settlements.table.headers.fte') },
    { id: 'totalHoursInGivenPeriod', label: t('page.settlements.table.headers.totalHoursInGivenPeriod') },
    { id: 'totalHoursPlanned', label: t('page.settlements.table.headers.totalHoursPlanned') },
    { id: 'totalOperatingHours', label: t('page.settlements.table.headers.totalOperatingHours') },
    { id: 'totalNonOperatingHours', label: t('page.settlements.table.headers.totalNonOperatingHours') },
    { id: 'totalFreeHours', label: t('page.settlements.table.headers.totalFreeHours') },
    { id: 'percentageHoursPlanned', label: t('page.settlements.table.headers.percentageHoursPlanned') },
    { id: 'percentageOperatingHours', label: t('page.settlements.table.headers.percentageOperatingHours') },
  ]

  const cellSx = { px: 1, py: 0.5, fontSize: '0.8rem' }

  const renderRow = (row: CollapsableRow) => {
    const getSrc = () => {
      if (row.employee !== null && row.employee.avatar !== null && row.employee.extension !== 'LINK') {
        return 'data:image/png;base64,' + row.employee.avatar
      } else if (row.employee !== null && row.employee.avatar !== null && row.employee.extension === 'LINK') {
        return atob(row.employee.avatar !== undefined ? row.employee.avatar : '')
      } else {
        return ''
      }
    }

    const src = getSrc()

    const getColor = () => {
      return row.totalMissingOrFreeHours < 0
        ? 'warning.main'
        : row.totalMissingOrFreeHours > 0
        ? 'accent.orange.main'
        : 'auto'
    }

    return (
      <>
        <TableCell align="left" sx={{ ...cellSx, pl: 0 }}>
          <Box display={'flex'} gap={'4px'} alignItems={'center'} sx={{ whiteSpace: 'nowrap' }}>
            <Avatar alt={row.employee.firstName} src={src} sx={{ width: 24, height: 24, flexShrink: 0 }} />
            {row.employee.firstName + ' ' + row.employee.lastName}
          </Box>
        </TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.employmentPercentage}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalHoursInGivenPeriod}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalHoursWorkedOrPlanned}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalOperatingHours}`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.totalNonOperatingHours}`}</TableCell>
        <TableCell
          align="center"
          sx={{
            ...cellSx,
            color: getColor(),
            fontWeight: 'bold'
          }}
        >{`${row.totalMissingOrFreeHours}`}</TableCell>
        <TableCell
          align="center"
          sx={{
            ...cellSx,
            color: getColor(),
            fontWeight: 'bold'
          }}
        >{`${row.percentageHoursWorkedOrPlanned}%`}</TableCell>
        <TableCell align="center" sx={cellSx}>{`${row.percentageOperatingHours}%`}</TableCell>
      </>
    )
  }

  const renderCollapsedRow = (row: Row, open: boolean) => {
    const workforceSchedule: WorkforceScheduleLightReturnDTO[] = row.workforceSchedule
    const workforceScheduleProcessed = workforceSchedule.map(workforceSchedule => {
      return {
        ...workforceSchedule,
        percent:
          (workforceSchedule.percent / 100) *
          getHoursForMonth(dayjs(workforceSchedule.date).format(DateFormat.YearMonth)),
      }
    })
    const mergedWorkforceSchedule = workforceScheduleProcessed.reduce<Record<string, WorkforceScheduleLightReturnDTO>>(
      (acc, current) => {
        const projectKey = current.project.id

        if (!acc[projectKey]) {
          acc[projectKey] = { ...current }
        } else {
          acc[projectKey].percent += current.percent
        }

        return acc
      },
      {}
    )
    const mergedWorkforceScheduleArray = Object.values(mergedWorkforceSchedule)

    return (
      <>
        <TransitionTableCell sx={{ padding: '0px', minWidth: '48px' }} open={open} />
        <TransitionTableCell open={open} />
        <TransitionTableCell open={open} sx={{ verticalAlign: 'top' }} colSpan={2}>
          <Typography fontWeight={700} sx={{ lineHeight: 'inherit' }}>
            {t('page.settlements.table.operationalProjects')}:
          </Typography>
          {mergedWorkforceScheduleArray
            .filter(value => value.project.operational)
            .map(value => (
              <Stack
                direction={'row'}
                sx={{ justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}
                key={value.id}
              >
                <Typography sx={{ lineHeight: 'inherit' }}>{`${value.project.name}`}</Typography>
                <Typography sx={{ lineHeight: 'inherit' }}>{`${value.percent.toFixed(1)}h`}</Typography>
              </Stack>
            ))}
        </TransitionTableCell>
        <TransitionTableCell open={open} sx={{ verticalAlign: 'top' }} colSpan={2}>
          <Typography fontWeight={700} sx={{ lineHeight: 'inherit' }}>
            {t('page.settlements.table.nonOperationalProjects')}:
          </Typography>
          {mergedWorkforceScheduleArray
            .filter(
              value =>
                !value.project.operational && !(value.project.name === 'Urlop' || value.project.name === 'Nadgodziny')
            )
            .map(value => (
              <Stack
                direction={'row'}
                sx={{ justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}
                key={value.id}
              >
                <Typography sx={{ lineHeight: 'inherit' }}>{`${value.project.name}`}</Typography>
                <Typography sx={{ lineHeight: 'inherit' }}>{`${value.percent.toFixed(1)}h`}</Typography>
              </Stack>
            ))}
        </TransitionTableCell>
        <TransitionTableCell sx={{ verticalAlign: 'top' }} open={open}>
          {mergedWorkforceScheduleArray
            .filter(value => value.project.name === 'Urlop' || value.project.name === 'Nadgodziny')
            .map(value => (
              <Stack
                direction={'row'}
                sx={{ justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}
                key={value.id}
              >
                <Typography sx={{ lineHeight: 'inherit' }}>{`${value.project.name}`}</Typography>
                <Typography sx={{ lineHeight: 'inherit' }}>{`${value.percent.toFixed(1)}h`}</Typography>
              </Stack>
            ))}
        </TransitionTableCell>
        <TransitionTableCell open={open} colSpan={3}></TransitionTableCell>
      </>
    )
  }

  const collapsableRows: CollapsableRow[] = settlements?.map((row, index) => ({
    ...row,
    index: index,
    hasCollapsedData: row.workHoursOrSchedule.length > 0,
    collapsedData: [
      {
        id: 'workforceSchedule-' + row.employee.id,
        workforceSchedule: row.workHoursOrSchedule,
        employmentPercentage: row.employmentPercentage,
      },
    ],
  }))
  return (
    <Stack>
      <CollapsibleTable
        columns={columns}
        collapsableRows={collapsableRows}
        renderRow={renderRow}
        renderCollapsedRow={renderCollapsedRow}
        renderFooter={() => renderFooter(settlements)}
      />
    </Stack>
  )
}

export default PlanTable
